import TalkRoomDomain from "@/domains/communityTalk/talkRoomDomain";
import { LayoutTagGetResponse } from "@/apiRequests/models/communityTalk/layoutTagGetResponse";
import { TalkRoomPostResponse } from "@/apiRequests/models/communityTalk/talkRoomPostResponse";
import { TalkRoomViewModel } from "@/viewModels/communityTalk/talkRoomViewModel";
import ApiValidationErrorUtil from "@/modules/utils/apiValidationErrorUtil";
import { ApiErrorViewModel, ApiValidationErrorViewModel } from "@/viewModels/apiErrorViewModel";
import { ReviewerAttributeAnswerPostRequest } from "@/apiRequests/models/communityTalk/reviewerAttributeAnswerPostRequest";
import { TalkRoomPostRequest } from "@/apiRequests/models/communityTalk/talkRoomPostRequest";
import StringUtil from "@/modules/utils/stringUtil";
import { PostMediaViewModel } from "@/viewModels/communityTalk/postMediaViewModel";
import { ReviewerAttributeDto } from "@/apiRequests/models/communityTalk/talkRoomGetResponse";

export default class TalkRoomController {
	private readonly _talkRoomDomain: TalkRoomDomain;

	/**
	 * コンストラクタ。
	 */
	constructor() {
		this._talkRoomDomain = new TalkRoomDomain();
	}

	/**
	 * トークルーム取得APIへのリクエスト。
	 */
	public async getTalkRoomData(
		tenantId?: string,
		tagCd?: string,
		subjectCd?: string,
		count?: number,
		displayTalkRoomId?: string,
		talkRoomSortType?: string,
		lastTalkRoomSortKey?: string
	): Promise<{ viewModel: TalkRoomViewModel; postableReviewerAttributes: ReviewerAttributeDto[] }> {
		// Null許容不可能な値がNullである場合、Error
		if (!tenantId) {
			throw new Error("tenantId: value not exist.");
		}
		if (!tagCd) {
			throw new Error("tagCd: value not exist.");
		}
		if (!subjectCd) {
			throw new Error("subjectCd: value not exist.");
		}
		if (!count) {
			throw new Error("count: value not exist.");
		}
		if (!talkRoomSortType) {
			throw new Error("talkRoomSortType: value not exist.");
		}

		// displayTalkRoomIdがある場合は、Countを100に設定
		if (displayTalkRoomId) {
			count = 100;
		}

		try {
			const response = await this._talkRoomDomain.getTalkRoomData(tenantId, tagCd, subjectCd, count, talkRoomSortType, lastTalkRoomSortKey);

			// displayTalkRoomIdがある場合は、該当のトークルームのみを取得
			if (displayTalkRoomId) {
				response.data.talkRooms = response.data.talkRooms.filter((talkroom) => talkroom.displayTalkRoomId === displayTalkRoomId);
			}

			// 返却値を成型
			const viewModel: TalkRoomViewModel = {
				talkRooms: response.data.talkRooms.map((e) => {
					return {
						...e,
						replies: [], // トークルーム取得段階ではリプライ件数は0件
					};
				}),
				talkRoomCount: response.data.talkRoomCount,
				talkRoomCountWithReply: response.data.talkRoomCountWithReply,
			};
			return { viewModel: viewModel, postableReviewerAttributes: response.data.reviewerAttribute };
		} catch (e) {
			throw new Error();
		}
	}

	/**
	 * 表示タグ設定取得APIへのリクエスト。
	 */
	public async getLayoutTagData(tenantId?: string, tagCd?: string): Promise<LayoutTagGetResponse> {
		// Null許容不可能な値がNullである場合、Error
		if (!tenantId) {
			throw new Error("tenantId: value not exist.");
		}
		if (!tagCd) {
			throw new Error("tagCd: value not exist.");
		}

		try {
			return await this._talkRoomDomain.getLayoutTagData(tenantId, tagCd);
		} catch (e) {
			throw new Error();
		}
	}

	/**
	 * トークルーム投稿APIへのリクエスト。
	 */
	public async postTalkRoomData(
		nickName: string,
		reviewerAttributeAnswers: ReviewerAttributeAnswerPostRequest[],
		profilePicIdTmp: string,
		medias: PostMediaViewModel[],
		tenantId?: string,
		tagCd?: string,
		subjectCd?: string,
		subjectName?: string,
		talkRoomUrl?: string,
		contents?: string,
		publishCookieId?: string,
		siteUserId?: string
	): Promise<TalkRoomPostResponse> {
		// Null許容不可能な値がNullである場合、Error
		if (!tenantId) {
			throw new Error("tenantId: value not exist.");
		}
		if (!tagCd) {
			throw new Error("tagCd: value not exist.");
		}
		if (!subjectCd) {
			throw new Error("subjectCd: value not exist.");
		}
		if (!publishCookieId) {
			throw new Error("publishCookieId: value not exist.");
		}

		try {
			// APIリクエスト用にデータ成形
			const request: TalkRoomPostRequest = {
				tenantId: tenantId,
				tagCd: tagCd,
				subjectCd: subjectCd,
				subjectName: !StringUtil.isNullOrEmpty(subjectName) ? (subjectName as string) : null,
				talkRoomUrl: !StringUtil.isNullOrEmpty(talkRoomUrl) ? (talkRoomUrl as string) : null,
				contents: contents ?? "",
				nickName: nickName,
				reviewerAttributeAnswers: reviewerAttributeAnswers,
				profilePicIdTmp: profilePicIdTmp,
				publishCookieId: publishCookieId,
				tenantMemberCd: siteUserId,
				movies: medias
					.filter((e) => e.type === "Movie")
					.map((e) => ({
						movieId: e.mediaIdTmp,
						thumbnailIndex: e.movieThumbnailIndex as number,
					})),
				images: medias
					.filter((e) => e.type === "Image")
					.map((e) => ({
						imageId: e.mediaIdTmp,
					})),
			};
			return await this._talkRoomDomain.postTalkRoomData(request, tenantId);
		} catch (e) {
			// ユーザー起因のバリデーションエラーであればバリデーションエラーメッセージとして返却
			if (ApiValidationErrorUtil.isValidateObject_talkRoomPost(e)) {
				const errorResult: ApiErrorViewModel = {
					isValidationError: true,
					validationErrorContents: e as ApiValidationErrorViewModel,
					errorContents: e,
				};
				throw errorResult;
			} else {
				// そうでなければエラーオブジェクトをそのまま返却
				const errorResult: ApiErrorViewModel = {
					isValidationError: false,
					validationErrorContents: null,
					errorContents: e,
				};
				throw errorResult;
			}
		}
	}
}
